import React, { useState, useEffect } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { useIntl, getLocale, history, useDispatch, useLocation } from 'umi';
import { Card, Button } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import Abnormal from '@home/components/Abnormal';
import ReportUploader from '@home/components/ReportUploader';
import { homeHonorTopChartsNew } from '@home/services/home';
import { recognizeReportService } from '@home/services/report';
import { openPDFPreviewDownloadPage } from '@intelligence/components/ReportCard/utils';
import { setName } from '@home/pages/home/Explore/_config/common';
import { reportOverviewPath } from '@home/utils/urlConfig';
import { getDBIReportListPagePath, getReportResourcePath } from '@intelligence/utils/reports';
import { REPORT_DATA_MODEL_NAMESPACE } from '@home/models/report';
import { useReportConfig } from '@intelligence/hooks/useReportConfig';
import { resolveReportConfig } from '@intelligence/utils/reportConfig';
import { getDatasourceDisplay } from '@intelligence/pages/ReportSearch/utils';

import './UnloadModal.scss';

import ICON_PLUS from '@home/assets/svg/common/plus.svg';

interface IReportUploaderPlusProps extends IBaseProps {
  triggerOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ReportUploaderPlus: React.FC<IReportUploaderPlusProps> = (props) => {
  const { style, className, triggerOpen, onOpenChange } = props;

  const { formatMessage } = useIntl();

  const dispatch = useDispatch();

  return (
    <ReportUploader
      cancelable
      showLinker
      showNotificationWhenUploadHTMLOrZipFile
      recognize={recognizeReportService}
      triggerOpen={triggerOpen}
      onOpenChange={onOpenChange}
      onRecognized={(file, result) => {
        dispatch({ type: `${REPORT_DATA_MODEL_NAMESPACE}/updateReportRecognizeResult`, payload: { file, result } });
        history.push('/user-center/content-management-center/reports/add');
      }}
      button={
        <Button style={style} className={classnames('dbh-home-report-uploader-btn', className)} type="primary">
          {/* <SvgIcon type="plus" /> */}
          <img className="dbh-home-report-uploader-btn-icon" src={ICON_PLUS} alt="" />
          <span>{formatMessage({ id: 'dbu_report_uploader_title' })}</span>
        </Button>
      }
    />
  );
};

const NewReports = () => {
  const location = useLocation();
  const locale = getLocale();
  const isCN = locale === 'zh-CN';
  const { formatMessage } = useIntl(); // 中英切换方法
  const reportConfig = useReportConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reports, setReports] = useState([]);
  const [noData, setNoData] = useState(false);
  const [triggerUploadModal, setTriggerUploadModal] = useState(false);

  const getNewReports = async () => {
    if (loading) return;
    setLoading(true);
    try {
      // const startDate = moment().startOf('months').subtract(1, 'month').format('YYYY-MM-DD');
      const endDate = moment().format('YYYY-MM-DD');
      const res = await homeHonorTopChartsNew({
        'Top New Reports': {
          top: 5,
          ignore_tech_center: true,
          report_types: [
            'intelligence_ai',
            'intelligence',
            'intelligence_insights',
            'intelligence_iic',
            'gameResearch',
          ],
          // start_date: startDate,
          end_date: endDate,
        },
      });
      const data = res['Top New Reports'] || [];
      if (!data?.length) {
        setNoData(true);
      } else {
        setReports(data || []);
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (data: any) => {
    const pdfPreviewDownloadPage = '/pdfPreviewDownloadLikeShare';
    const downloadLogKeyObject: any = {
      intelligence_ai: 'aiHubReportsDownload',
      intelligence: 'MarketReportsDownload',
      intelligence_insights: 'insightsReportsDownload',
      intelligence_iic: 'insightsCenterLibraryDownload',
      gameResearch: 'gameResearchDownload',
    };
    const openParms = {
      id: data?.id,
      systemId: data?.system_id,
      resourceId: data.resource_id,
      resourceName: isCN ? data?.title_cn : data?.title_en,
      resourcePath: getReportResourcePath(locale, data),
      resourceLike: data?.report_like,
      downloadLogKey: downloadLogKeyObject[data?.system_id],
      lang: isCN ? 'cn' : 'en',
      referer: getDBIReportListPagePath(data?.system_id, data?.source),
    } as any;
    openPDFPreviewDownloadPage(pdfPreviewDownloadPage, openParms);
  };

  // Market Reports 显示 source 字段，其他显示创作者名称
  const getReportSourceName = (item: any): string => {
    if (item?.system_id === 'intelligence') {
      const { display_source: source = '' } = resolveReportConfig(item, reportConfig);
      return getDatasourceDisplay(source)?.name || source || '';
    }
    return isCN ? item?.create_name_cn : item?.create_name_en;
  };

  const ReportItem = (item: any) => {
    return (
      <div className={'dbh-new-report-item'} onClick={() => handleClick(item)}>
        <img src={item?.report_picture} className={'dbh-report-item-img'} />
        <div className={'dbh-report-item-right'}>
          <div className={'dbh-report-item-link'}>{isCN ? item?.title_cn : item?.title_en}</div>
          <div className={'dbh-report-item-desc'}>
            <span>{moment(item?.create_time).format('YYYY-MM-DD')}</span>
            <span className={'dbh-report-item-line'}>|</span>
            <span>{setName(getReportSourceName(item))}</span>
          </div>
        </div>
      </div>
    );
  };

  // const ReportList = () => {
  //   return <>{reports.map((ele: any) => ReportItem(ele))}</>;
  // };

  useEffect(() => {
    getNewReports();
  }, []);

  // Handle URL-triggered modal opening
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const action = searchParams.get('action');

    if (action === 'upload-report') {
      setTriggerUploadModal(true);

      // Clean up URL parameter
      const newSearchParams = new URLSearchParams(location.search);
      newSearchParams.delete('action');
      const newSearch = newSearchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: newSearch ? `?${newSearch}` : '',
      });
    }
  }, [location.search]);

  return (
    <Card className={'home-card'}>
      <Card.Body
        style={{ padding: '20px 6px 10px' }}
        title={
          <div className="home-table-flex" onClick={() => history.push(reportOverviewPath)}>
            <div className="home-table-title dbh-fix-less-padding">{formatMessage({ id: 'dbh_home_new_reports' })}</div>
            <SvgIcon type="arrow-right" className="home-table-icon" />
          </div>
          // <>
          //   <div className="home-table-title dbh-home-title-padding">

          //   </div>
          //   <SvgIcon type="arrow-right" className="home-table-icon" />
          // </>
        }
      >
        <Abnormal
          isLoading={loading}
          isError={error}
          isNoData={noData}
          style={{
            height: 200,
          }}
          component={
            <>
              {reports.map((ele: any) => ReportItem(ele))}
              <div className="dbh-home-report-uploader-btn-wrapper">
                <ReportUploaderPlus
                  triggerOpen={triggerUploadModal}
                  onOpenChange={(open) => {
                    if (!open) {
                      setTriggerUploadModal(false);
                    }
                  }}
                />
              </div>
            </>
          }
          // component={<ReportList />}
        />
      </Card.Body>
    </Card>
  );
};

export default NewReports;
